// Client portal — the message thread with the company. One thread; the
// company's replies show up here (polled) and the client can write back.
// Concurrency: every GET carries a revision and only the newest applies;
// polling pauses while a send is in flight; the send result is merged by id.
import { ArrowLeft, MessageSquare, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PortalShell } from "../components/PortalShell";
import { ApiError, portal, type ClientMessage } from "../lib/api";

const POLL_MS = 20_000;
const MAX_CHARS = 4000;

function when(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

function mergeById(cur: ClientMessage[], incoming: ClientMessage[]): ClientMessage[] {
  const map = new Map(cur.map((m) => [m.id, m]));
  for (const m of incoming) map.set(m.id, m);
  return [...map.values()].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
}

export default function PortalMessagesPage() {
  const nav = useNavigate();
  const [messages, setMessages] = useState<ClientMessage[] | null>(null);
  const [company, setCompany] = useState<string>("your team");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const alive = useRef(true);
  const rev = useRef(0);
  const mutating = useRef(false);
  const loading = useRef(false); // a poll never stacks on a slow GET
  const thread = useRef<HTMLDivElement | null>(null);
  const firstScroll = useRef(true);

  function nearBottom(): boolean {
    const el = thread.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }
  function scrollToBottom() {
    const el = thread.current;
    if (el) el.scrollTop = el.scrollHeight;
  }

  async function load(): Promise<void> {
    const r = ++rev.current;
    loading.current = true;
    try {
      const res = await portal.listMessages();
      if (!alive.current || r !== rev.current) return;
      setMessages(res.messages);
      if (res.company_name) setCompany(res.company_name);
      setLoadErr(null);
      // Sample the reader's position now, not when the request started.
      if (firstScroll.current || nearBottom()) { firstScroll.current = false; requestAnimationFrame(scrollToBottom); }
    } catch (e: any) {
      if (!alive.current || r !== rev.current) return;
      if (e instanceof ApiError && e.status === 401) { nav("/portal/login", { replace: true }); return; }
      setMessages((cur) => cur ?? []);
      setLoadErr(e?.message || "Couldn't load your messages.");
    } finally { if (r === rev.current) loading.current = false; }
  }

  useEffect(() => {
    alive.current = true;
    void load();
    const t = window.setInterval(() => { if (alive.current && !mutating.current && !loading.current && !document.hidden) void load(); }, POLL_MS);
    return () => { alive.current = false; rev.current++; window.clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function send() {
    const text = draft.trim();
    if (!text || text.length > MAX_CHARS) return;
    mutating.current = true; setBusy(true); setActionErr(null); setSent(null);
    try {
      const r = await portal.sendMessage(text);
      if (!alive.current) return;
      setMessages((cur) => mergeById(cur ?? [], [r.message]));
      setDraft("");
      setSent("Sent. A specialist will reply here.");
      requestAnimationFrame(scrollToBottom);
    } catch (e: any) {
      if (!alive.current) return;
      if (e instanceof ApiError && e.status === 401) { nav("/portal/login", { replace: true }); return; }
      setActionErr(e?.body?.message || e?.message || "Couldn't send your message. Try again in a moment.");
    } finally {
      mutating.current = false;
      if (alive.current) { setBusy(false); void load(); }
    }
  }

  const chars = draft.trim().length;

  return (
    <PortalShell>
      <section className="portal-section">
        <p className="portal-eyebrow">Messages</p>
        <h1 className="portal-headline">Talk to {company}.</h1>
        <p className="portal-sub">
          Ask a question or send an update. Replies show up right here, and you'll get an email when one is waiting.
        </p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
          <Link to="/portal/dashboard" className="portal-btn-ghost">
            <ArrowLeft size={14} aria-hidden="true" /> Back to dashboard
          </Link>
        </div>
      </section>

      <article className="portal-card">
        <div className="portal-card-heading">
          <h2><MessageSquare size={18} aria-hidden="true" style={{ verticalAlign: "-3px", marginRight: 6 }} />Your conversation</h2>
        </div>

        {loadErr && (
          <p className="portal-error" role="alert">
            {loadErr} <button type="button" className="portal-btn-ghost" onClick={() => void load()} disabled={busy}>Retry</button>
          </p>
        )}

        <div ref={thread} className="portal-messages__thread" role="log" aria-live="polite" aria-label="Message thread">
          {messages === null && <p className="portal-sub on-card">Loading…</p>}
          {messages && messages.length === 0 && (
            <p className="portal-sub on-card" style={{ fontSize: 13 }}>No messages yet. Write the first one below.</p>
          )}
          {messages && messages.map((m) => {
            const mine = m.sender === "client";
            return (
              <div key={m.id} className={`portal-messages__row ${mine ? "is-me" : "is-them"}`}>
                <div className="portal-messages__bubble">
                  <div className="portal-messages__meta">
                    <strong>{mine ? "You" : (m.author_name || company)}</strong>
                    <span>{when(m.created_at)}</span>
                  </div>
                  <div className="portal-messages__body">{m.body}</div>
                </div>
              </div>
            );
          })}
        </div>

        {actionErr && <p className="portal-error" role="alert">{actionErr}</p>}
        {sent && <p className="portal-sub on-card" role="status" style={{ fontSize: 13 }}>{sent}</p>}

        <div className="portal-messages__composer">
          <textarea
            className="portal-messages__input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Type your message…"
            aria-label="New message"
            disabled={busy}
            maxLength={MAX_CHARS}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); void send(); } }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <span className="portal-sub on-card" style={{ fontSize: 12 }}>{chars ? `${chars.toLocaleString()} / ${MAX_CHARS.toLocaleString()}` : "Please don't include your full Social Security number here."}</span>
            <button className="portal-btn-primary" onClick={send} disabled={busy || !chars || chars > MAX_CHARS}>
              <Send size={16} aria-hidden="true" /> {busy ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </article>
    </PortalShell>
  );
}
